import React from 'react'
import styled from 'styled-components'
import {
  zoning_indice_sources,
  zoning_sources_agreement,
  ZoningMetricSources,
} from '@popety-io/types'
import { MetricSourceBadge } from '../../../atoms/MetricSourceBadge'
import { getVisibleSources, getConflictValue } from './utils/metricTableHelpers'

const BadgeLineContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  line-height: 1;
  margin-top: 2px;
`

const AgreementSymbol = styled.span<{ $type: 'identical' | 'conflict' }>`
  font-size: 10px;
  font-weight: 700;
  color: ${({ $type }) => ($type === 'identical' ? '#198754' : '#dc3545')};
`

const ConflictStrike = styled.span`
  font-size: 11px;
  color: #868e96;
  text-decoration: line-through;
`

interface SourceBadgeLineProps {
  defaultSource?: zoning_indice_sources
  agreement?: zoning_sources_agreement
  sources?: ZoningMetricSources
  metricKey: string
  isOverridden?: boolean
}

/**
 * Renders the badge line below the authorized value in the table.
 * - Override (user edit or `manual` source): EDIT badge
 * - Unique: single badge
 * - Identical: GEO = REG (both badges with green =)
 * - Conflict: GEO ≠ REG 5.00 m (both badges with red ≠ + strikethrough value)
 */
export const SourceBadgeLine: React.FC<SourceBadgeLineProps> = ({
  defaultSource,
  agreement,
  sources,
  metricKey,
  isOverridden,
}) => {
  // User override (saved edit or `manual` source): one EDIT badge
  if (isOverridden || defaultSource === 'manual') {
    return (
      <BadgeLineContainer>
        <MetricSourceBadge source="manual" />
      </BadgeLineContainer>
    )
  }

  if (!defaultSource) return null

  const visible = getVisibleSources(sources)

  // Single source (unique) or only one visible source
  if (agreement === 'unique' || visible.length <= 1) {
    return (
      <BadgeLineContainer>
        <MetricSourceBadge source={defaultSource} />
      </BadgeLineContainer>
    )
  }

  // Two sources: identical or conflict
  // Show badges in priority order: GEO first, then REG
  const orderedSources = visible
    .filter((s) => s !== 'manual')
    .sort((a, b) => {
      const priority: Record<string, number> = {
        geoportal: 0,
        llm: 1,
        estimate: 2,
      }
      return (priority[a] ?? 9) - (priority[b] ?? 9)
    })

  if (agreement === 'identical') {
    return (
      <BadgeLineContainer>
        {orderedSources.map((src, i) => (
          <React.Fragment key={src}>
            {i > 0 && <AgreementSymbol $type="identical">=</AgreementSymbol>}
            <MetricSourceBadge source={src} />
          </React.Fragment>
        ))}
      </BadgeLineContainer>
    )
  }

  if (agreement === 'conflict') {
    const conflictData = getConflictValue(sources, defaultSource, metricKey)
    return (
      <BadgeLineContainer>
        {orderedSources.map((src, i) => (
          <React.Fragment key={src}>
            {i > 0 && <AgreementSymbol $type="conflict">≠</AgreementSymbol>}
            <MetricSourceBadge source={src} />
          </React.Fragment>
        ))}
        {conflictData && <ConflictStrike>{conflictData.value}</ConflictStrike>}
      </BadgeLineContainer>
    )
  }

  // Fallback: just show default source
  return (
    <BadgeLineContainer>
      <MetricSourceBadge source={defaultSource} />
    </BadgeLineContainer>
  )
}
